// AJAX add-to-cart for custom cards/tiers: [data-j-add="variantId"] or [data-j-add-items='[{"id":1,"quantity":1}]'].
// Opens Dawn's cart drawer with the re-rendered sections (same contract as product-form.js).

// Supliful quantity mode: Supliful can only fulfil its own single products, so any add-to-cart request that contains
// a bundle variant (window.jBundleMap, rendered in theme.liquid) is rewritten to add the bundle's component products
// instead. Patching fetch covers every entry point at once: product form, quick add, CLP buttons, cards, this file.
(() => {
  const map = window.jBundleMap;
  if (!map || !Object.keys(map).length || window.__jBundleFetch) return;
  window.__jBundleFetch = true;
  const nativeFetch = window.fetch.bind(window);
  const isAdd = (url) => /\/cart\/add(\.js)?(\?|$)/.test(String(url instanceof Request ? url.url : url));
  const readBody = (body) => {
    if (body instanceof FormData) {
      const o = {};
      for (const [k, v] of body.entries()) if (typeof v === 'string') o[k] = v;
      return { items: [{ id: o.id, quantity: o.quantity || 1, ...(o.selling_plan ? { selling_plan: o.selling_plan } : {}) }], sections: o.sections, sections_url: o.sections_url };
    }
    if (typeof body === 'string') {
      try { const j = JSON.parse(body); return j.items ? j : { ...j, items: [{ id: j.id, quantity: j.quantity || 1 }] }; } catch (e) { return null; }
    }
    return null;
  };
  window.fetch = async (url, init = {}) => {
    if (!isAdd(url) || (init.method || 'GET').toUpperCase() !== 'POST') return nativeFetch(url, init);
    const req = readBody(init.body);
    if (!req || !req.items.some((i) => map[String(i.id)])) return nativeFetch(url, init);
    const items = req.items.flatMap((i) => (map[String(i.id)] || [i]).map((c) =>
      map[String(i.id)] ? { id: c.id, quantity: c.quantity * Number(i.quantity || 1) } : c));
    const body = { items };
    if (req.sections) body.sections = req.sections;
    if (req.sections_url) body.sections_url = req.sections_url;
    const res = await nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    // product-form.js and the cart drawer expect a single-item response: put the first item's fields on top
    const merged = data.items ? { ...data.items[0], ...data } : data;
    return new Response(JSON.stringify(merged), { status: res.status, headers: { 'Content-Type': 'application/json' } });
  };
})();

// Subscribe & Save: purchase-options.js puts the subscription line items (the hidden subscribable products with their
// selling plans, see snippets/purchase-options.liquid) into a j_sub_items form field. Any add-to-cart request that
// carries it adds those items instead of the product's own variant.
(() => {
  if (window.__jSubFetch) return;
  window.__jSubFetch = true;
  const prevFetch = window.fetch.bind(window);
  const isAdd = (url) => /\/cart\/add(\.js)?(\?|$)/.test(String(url instanceof Request ? url.url : url));
  window.fetch = async (url, init = {}) => {
    const body = init.body;
    if (!isAdd(url) || !(body instanceof FormData) || !body.get('j_sub_items')) return prevFetch(url, init);
    let items;
    try { items = JSON.parse(body.get('j_sub_items')); } catch (e) { return prevFetch(url, init); }
    const json = { items };
    if (body.get('sections')) json.sections = body.get('sections');
    if (body.get('sections_url')) json.sections_url = body.get('sections_url');
    const res = await prevFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(json),
    });
    const data = await res.json();
    // product-form.js and the cart drawer expect a single-item response: put the first item's fields on top
    const merged = data.items ? { ...data.items[0], ...data } : data;
    return new Response(JSON.stringify(merged), { status: res.status, headers: { 'Content-Type': 'application/json' } });
  };
})();

(() => {
  async function addItems(items, button) {
    const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items };
    if (drawer && drawer.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    button?.setAttribute('aria-busy', 'true');
    button?.classList.add('loading');
    try {
      const res = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      if (drawer && drawer.renderContents) {
        drawer.classList.remove('is-empty');
        // renderContents expects the first added item's key/id plus sections
        drawer.renderContents({ ...data.items?.[0], ...data, sections: data.sections });
      } else {
        window.location = window.routes.cart_url;
      }
    } catch (e) {
      alert(e.message || 'Could not add to cart');
    } finally {
      button?.removeAttribute('aria-busy');
      button?.classList.remove('loading');
    }
  }

  // Landing-page level "One-time / Subscribe & Save" toggle (sections/clp-tier.liquid): swaps the shown price and
  // makes the level's button add its subscription items instead of the bundle
  document.addEventListener('click', (e) => {
    const m = e.target.closest('.j-clp-mode__btn');
    if (!m) return;
    const cta = m.closest('.j-clp-tier__cta');
    const sub = m.dataset.mode === 'sub';
    cta.querySelectorAll('.j-clp-mode__btn').forEach((b) => { b.classList.toggle('is-active', b === m); b.setAttribute('aria-pressed', b === m); });
    const btn = cta.querySelector('[data-sub-items]');
    if (btn) btn.dataset.subActive = sub;
    const p = cta.querySelector('.j-clp-tier__price[data-sub-price]');
    if (p) {
      p.querySelector('strong').textContent = sub ? p.dataset.subPrice : p.dataset.onePrice;
      const per = p.querySelector('.j-clp-tier__per'); if (per) per.hidden = !sub;
      const save = sub ? p.dataset.subSave : p.dataset.oneSave;
      const badge = p.querySelector('.j-badge'); if (badge) { badge.textContent = `Save ${save}%`; badge.hidden = !(Number(save) > 0); }
    }
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-j-add], [data-j-add-items]');
    if (!btn) return;
    e.preventDefault();
    if (btn.dataset.subActive === 'true' && btn.dataset.subItems) { addItems(JSON.parse(btn.dataset.subItems), btn); return; }
    const items = btn.dataset.jAddItems
      ? JSON.parse(btn.dataset.jAddItems)
      : [{ id: Number(btn.dataset.jAdd), quantity: 1, ...(btn.dataset.plan ? { selling_plan: Number(btn.dataset.plan) } : {}) }];
    addItems(items, btn);
  });

  window.jAddItems = addItems;
})();
